<template>
  <div class="content mb-5">
    <b-row>
      <b-col class="mb-3">
        <h2>{{ $t("Location Details") }}</h2>
      </b-col>
    </b-row>

    <div v-if="!loading">
      <component
        :is="`t-site-${selectedLocation.type}`"
        :policy-options="policyOptions"
        :policy-radio="policyRadio"
        :status-options="statusOptions"
        :loading="loading"
        :location-type="locationType"
        @scroll="onScroll"
      ></component>
    </div>

    <b-row v-if="loading" class="mb-5">
      <b-col cols="12" md="6">
        <div class="mb-5">
          <b-skeleton height="24px" animation="wave" width="20%"></b-skeleton>
          <b-skeleton height="38px" animation="wave" width="100%"></b-skeleton>
        </div>
        <div class="mb-5">
          <b-skeleton height="24px" animation="wave" width="20%"></b-skeleton>
          <b-skeleton height="38px" animation="wave" width="100%"></b-skeleton>
        </div>
        <div class="mb-5">
          <b-skeleton height="24px" animation="wave" width="20%"></b-skeleton>
          <b-skeleton height="38px" animation="wave" width="100%"></b-skeleton>
        </div>
      </b-col>
      <b-col cols="12" md="6">
        <div class="mb-5">
          <b-skeleton height="24px" animation="wave" width="20%"></b-skeleton>
          <b-skeleton height="38px" animation="wave" width="100%"></b-skeleton>
        </div>
        <div class="mb-5">
          <b-skeleton height="24px" animation="wave" width="20%"></b-skeleton>
          <b-skeleton height="38px" animation="wave" width="100%"></b-skeleton>
        </div>
        <div class="mb-5">
          <b-skeleton height="24px" animation="wave" width="20%"></b-skeleton>
          <b-skeleton height="38px" animation="wave" width="100%"></b-skeleton>
        </div>
      </b-col>
    </b-row>

    <div v-if="!loading && selectedLocation.type === 'virtual'">
      <b-row class="mb-5">
        <b-col>
          <t-data-table
            hoverable
            disable-row-select
            data-test-id="sites-site-details-data-table"
            :filter-placeholder="$t('Search Devices')"
            :title="$t('Devices')"
            :table-data="dataSet"
            :actions="tableActions"
            :loading="loading"
            user-role="customer"
            @on-export="onExport"
            @on-row-clicked="onRowClicked"
            :searchExports="searchExports"
            @export-headers="exportedHeaders"
          />
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<route>
{
  "name": "Customer Environment Location",
  "meta": {
    "authorize" : [1, 3, 4]
  }
}
</route>

<script>
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { mapActions, mapGetters } from "vuex";

dayjs.extend(relativeTime);

export default {
  layout: "customer",
  components: {
    "t-site-virtual": () => import("@/forms/TSiteVirtual.vue"),
    "t-site-staticip": () => import("@/forms/TSiteStaticip.vue"),
    "t-site-dynamicip": () => import("@/forms/TSiteDynamicip.vue"),
    "t-site-dynamicdns": () => import("@/forms/TSiteDynamicdns.vue"),
    "t-site-roaming": () => import("@/forms/TSiteRoaming.vue"),
  },
  data() {
    return {
      loading: true,
      currentHeaders: null,
      locationType: "",
      dataOptions: {
        results: 10,
        page: 1,
        sortBy: "name",
        sortDesc: [false],
        filter: null,
      },
      currentPage: null,
      previousScrollPosition: null,
      policyOptions: [],
      statusOptions: [],
      searchExports: ["name", "user", "location", "status", "policyname"],
      tableActions: {
        add: { enabled: false },
        export: { enabled: true },
        import: { enabled: false },
        delete: { enabled: false },
      },
      dataSet: {
        fields: [
          { value: "name", text: "Device Name", sortable: true },
          { value: "key", text: "Device Key", sortable: true },
          { value: "user", text: "User", sortable: true },
          { value: "os", text: this.$t("Agent"), sortable: true },
          { value: "location", text: "Virtual Location", sortable: true },
          { value: "status", text: "Status", sortable: true },
          { value: "policyname", text: "Policy", sortable: true },
        ],
        items: [],
      },
      allowedTypes: [
        "staticip",
        "virtual",
        "dynamicip",
        "dynamicdns",
        "roaming",
      ],
      policyRadio: [
        {
          text: this.$t("Inherited"),
          subtitle: this.$t("Inherit default policy"),
          tooltip: this.$t("Inherit default policy"),
          testID: "location-policy-inherited",
          value: "None",
          checked: false,
          icon: "layer-backward",
          canCustomise: false,
          canDropDownSelect: false,
        },
        {
          text: this.$t("Specific"),
          subtitle: this.$t("Use a specific policy"),
          tooltip: this.$t("Use a specific"),
          testID: "location-policy-specific",
          value: "Policy",
          checked: false,
          icon: "pencil-square",
          canCustomise: false,
          canDropDownSelect: true,
        },
      ],
    };
  },
  methods: {
    ...mapActions("pageMeta", ["setPageTitle"]),
    ...mapActions("locations", ["fetchLocation", "updateLocation"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("customerPolicies", [
      "fetchCustomerPolicies",
      "fetchScrollCustomerPolicies",
      "updateCustomerPolicy",
    ]),
    ...mapActions("devices", ["fetchDevices"]),
    onRowClicked(item) {
      this.$router.push(`/customer/environment/devices/${item.id}`);
    },
    exportedHeaders(headers) {
      this.currentHeaders = headers;
    },
    onExport(items) {
      if (items.length) {
        const exportData = items.map((res) => {
          let payload = {};
          this.currentHeaders.forEach((headers) => {
            return (payload[headers.value] = res[headers.value]);
          });

          return payload;
        });
        this.$downloadCSV(exportData, "Locations");
      } else {
        this.displayToast({
          title: this.$t("Failure"),
          message: this.$t("No locations in table"),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    onScroll(e) {
      const { target } = e;
      let currentScrollPosition = target.scrollTop + target.scrollHeight;

      if (currentScrollPosition > this.previousScrollPosition) {
        if (
          Math.ceil(target.scrollTop) >=
            target.scrollHeight - target.offsetHeight &&
          this.dataOptions.page > this.currentPage
        ) {
          try {
            this.fetchScrollCustomerPolicies({
              id: this.selectedUser.id,
              ...this.dataOptions,
            });
          } catch (e) {
            console.log("e", e);
          } finally {
            this.currentPage = this.dataOptions.page;
            this.dataOptions.page++;
          }
        }
      }
      this.previousScrollPosition = currentScrollPosition;
    },
  },
  computed: {
    ...mapGetters("locations", ["locations", "selectedLocation"]),
    ...mapGetters("customerPolicies", ["customerPolicies"]),
    ...mapGetters("stats", ["locationStats"]),
    ...mapGetters("authentication", ["selectedUser"]),
    ...mapGetters("devices", ["userDevices"]),
  },
  watch: {
    selectedLocation() {
      this.setPageTitle(this.selectedLocation.name);
    },
    customerPolicies() {
      this.policyOptions = [
        ...this.customerPolicies.map((p) => {
          return { value: p.id, text: p.name };
        }),
      ];
    },
  },
  async created() {
    if (
      !this.$route.query.type ||
      !this.allowedTypes.includes(this.$route.query.type)
    ) {
      this.$router.push("/customer/environment/locations");
      return;
    }

    try {
      await Promise.all([
        this.fetchLocation({
          id: this.selectedUser.id,
          type: this.$route.query.type,
          locationId: this.$route.params.id,
        }),
        this.fetchCustomerPolicies({
          id: this.selectedUser.id,
          ...this.dataOptions,
        }),
      ]);
    } finally {
      if (this.selectedLocation.type === "virtual") {
        await this.fetchDevices({
          id: this.selectedUser.id,
          locationId: this.selectedLocation.id,
        });
        this.dataSet.items = this.userDevices;
      }

      this.policyOptions = [
        ...this.customerPolicies.map((p) => {
          return { value: p.id, text: p.name };
        }),
      ];

      this.statusOptions = [
        {
          value: null,
          text: this.$t("Please select an option"),
          disabled: true,
        },
        { value: 1, text: "API dependency" },
        // ...this.customerPolicies.map((p) => {
        //   return { value: p.id, text: p.name };
        // }),
      ];

      this.setPageTitle(this.selectedLocation.name);
      this.locationType = this.$sitesTypeLookup[this.selectedLocation.type];

      this.loading = false;
    }
  },
  unmounted() {
    this.setPageTitle(null);
  },
};
</script>

<style scoped lang="scss">
::v-deep .t-card-trend {
  > div {
    margin-bottom: 4.5rem;
  }
}
</style>
